import React from 'react';
import parse from 'html-react-parser';

import { Stack } from '@auspost/postmaster-react';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import { ArticleEvent, Milestone } from 'models/shipments';
import { getEventDateTimeFromISO } from 'utils/date.utils';

import MapAndHoursLink from '../MapAndHoursLink/MapAndHoursLink';
import { SCAN_EVENT_DESC_TEST_ID, SCAN_EVENT_LOCATION_TIME_TEST_ID, SCAN_EVENT_TEST_ID } from './ScanEvent.test-ids';

import styles from './ScanEvent.module.scss';

interface ScanEventProps {
  event: ArticleEvent;
  displayFootnote?: boolean;
  tooltipText?: string | null;
}
const ScanEvent: React.FC<ScanEventProps> = ({ event, displayFootnote, tooltipText }) => {
  const getEventA11yDesc = (eventName: string, eventDate: string, eventLocation: string | null) => {
    const { date, time } = getEventDateTimeFromISO(eventDate) ?? {};
    let result = `${eventName},`;
    if (eventLocation) result += `at ${eventLocation},`;
    if (date && time) result += `on ${date}, ${time}`;
    return result;
  };

  const displayLocationTimestamp = () => {
    const location = event?.location || '';
    const { date, time } = getEventDateTimeFromISO(event?.localeDateTime) ?? {};
    const timestamp = date && time ? `${date}, ${time}` : null;
    return location && timestamp ? `${location} • ${timestamp}` : `${location}${timestamp}`;
  };

  let tooltipContent = tooltipText;
  if (event?.hasLocationDualName && event.location?.includes('Boorna Wangkiny Mia')) {
    tooltipContent =
      'We acknowledge this facility, known as Boorna Wangkiny Mia, is culturally translated to mean ‘Home of the Message Stick’ in the traditional language of the Noongar Nation. The ‘Dual Naming’ was developed in respectful collaboration with Indigenous knowledge holders and demonstrates our ongoing commitment towards reconciliation.';
  }
  if (event?.hasLocationDualName && event.location?.toLowerCase().includes('mookin-yaba')) {
    tooltipContent = `The name of this facility, Mookin-Yaba, honours Charlie Moreton, the last messenger man to the Yuggera and Quandamooka Nations. It uses his traditional name ‘Mookin’ combined with ‘Yaba’, meaning message stick. The Dual Naming was developed in respectful collaboration with Indigenous knowledge holders and demonstrates our ongoing commitment towards reconciliation.`;
  }

  return (
    <li className={styles.step} data-testid={SCAN_EVENT_TEST_ID}>
      <div>
        <p className={styles.stepDescription} aria-hidden={true} data-testid={SCAN_EVENT_DESC_TEST_ID}>
          {event.description}
        </p>
        <Stack space='small'>
          <p className={styles.stepTimestamp} aria-hidden={true} data-testid={SCAN_EVENT_LOCATION_TIME_TEST_ID}>
            {displayLocationTimestamp()}
            {displayFootnote && <sup>1</sup>}
          </p>
          {event.milestone === Milestone.AWAITING_COLLECTION && <MapAndHoursLink />}
        </Stack>
        <p className='sr-only'>{getEventA11yDesc(event.description, event.localeDateTime, event.location)}</p>
      </div>
      {!!tooltipContent && (
        <Infotip anchorClassName={styles.tooltipAnchor} floatClassName={styles.tooltipFloat}>
          {parse(tooltipContent)}
        </Infotip>
      )}
    </li>
  );
};

export default ScanEvent;
